/**
 * Sentry Error Tracking Setup for Angular
 *
 * This module initializes Sentry for error tracking in Angular applications.
 * If sentryDsn is not configured in environment, Sentry will not be initialized
 * and the application will continue to work normally.
 */

import { ErrorHandler, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import * as Sentry from '@sentry/angular-ivy';

let sentryInitialized = false;

/**
 * Initialize Sentry error tracking
 * Call this before bootstrapping Angular application
 */
export function initSentry(): void {
  const dsn = (environment as any).sentryDsn;
  const sentryEnvironment = (environment as any).sentryEnvironment || 'local';

  // Skip initialization if DSN is not configured
  if (!dsn) {
    console.log('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  try {
    Sentry.init({
      dsn: dsn,
      environment: sentryEnvironment,

      // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
      // Adjust this value in production for better performance.
      tracesSampleRate: sentryEnvironment === 'prod' ? 0.1 : 1.0,

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });

    sentryInitialized = true;
    console.log(`[Sentry] Initialized for environment: ${sentryEnvironment}`);
  } catch (error: any) {
    console.error('[Sentry] Failed to initialize:', error.message);
  }
}

/**
 * Check if Sentry is initialized
 */
export function isSentryInitialized(): boolean {
  return sentryInitialized;
}

/**
 * Capture an exception and send it to Sentry
 */
export function captureException(error: Error, context: any = {}): void {
  if (!sentryInitialized) {
    console.error('[Error]', error);
    return;
  }

  Sentry.withScope((scope: Sentry.Scope) => {
    if (context.user) {
      scope.setUser(context.user);
    }
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value as string);
      });
    }
    if (context.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
}

/**
 * Custom ErrorHandler that reports errors to Sentry
 * Falls back to default console logging if Sentry is not initialized
 */
@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    const extractedError = this.extractError(error) || 'Handled unknown error';

    // Log to console for development
    console.error('[SentryErrorHandler]', extractedError);

    // Report to Sentry if initialized
    if (sentryInitialized) {
      Sentry.captureException(extractedError);
    }
  }

  private extractError(error: any): Error | null {
    // Try to unwrap zone.js error
    if (error && error.ngOriginalError) {
      error = error.ngOriginalError;
    }

    // Check if it's an ErrorEvent
    if (error instanceof ErrorEvent) {
      return error.error;
    }

    // Check if it's a string error
    if (typeof error === 'string') {
      return new Error(error);
    }

    return error;
  }
}

/**
 * Get providers for Sentry integration
 * Only provides Sentry if it's initialized
 */
export function getSentryProviders(): any[] {
  if (!sentryInitialized) {
    return [];
  }

  return [
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler,
    },
  ];
}
